import {
  getEstablishmentProfile,
  getHospitalRevenueSettings,
  getPharmacySettings,
  getReceptionSettings,
  hasSurgeryMedicalUnit,
  listMedicalUnits,
  subscribeSettingsChange,
} from "@/core/admin/establishment-store";
import { listHospitalPartnerships } from "@/core/insurance/partnership-store";
import { getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";

const SETUP_KEY = "hospitol.setup.state";
const SETUP_EVENT = "hospitol-setup-changed";

export interface HospitolSetupState {
  setupCompleted: boolean;
  currentStep: number;
  insuranceSkipped: boolean;
  completedAt: string | null;
}

export interface HospitolSetupChecks {
  establishment: boolean;
  medicalUnits: boolean;
  surgeryUnit: boolean;
  reception: boolean;
  hospitalRevenue: boolean;
  pharmacy: boolean;
  insurance: boolean;
}

export interface HospitolSetupStatus {
  complete: boolean;
  currentStep: number;
  checks: HospitolSetupChecks;
  progressPercent: number;
}

const DEFAULT_STATE: HospitolSetupState = {
  setupCompleted: false,
  currentStep: 1,
  insuranceSkipped: false,
  completedAt: null,
};

function readState(): HospitolSetupState {
  try {
    const raw = getStorageItem(SETUP_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<HospitolSetupState>;
    return {
      setupCompleted: Boolean(parsed.setupCompleted),
      currentStep: Math.min(6, Math.max(1, Number(parsed.currentStep ?? 1))),
      insuranceSkipped: Boolean(parsed.insuranceSkipped),
      completedAt: parsed.completedAt ? String(parsed.completedAt) : null,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function writeState(state: HospitolSetupState): void {
  setStorageItem(SETUP_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(SETUP_EVENT));
}

export function subscribeSetupChange(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(SETUP_EVENT, handler);
  const unsubSettings = subscribeSettingsChange(handler);
  const unsubPartnerships = () => {
    window.removeEventListener("hospitol:insurance-partnerships", handler);
  };
  window.addEventListener("hospitol:insurance-partnerships", handler);
  return () => {
    window.removeEventListener(SETUP_EVENT, handler);
    unsubSettings();
    unsubPartnerships();
  };
}

export function computeSetupChecks(state: HospitolSetupState): HospitolSetupChecks {
  const profile = getEstablishmentProfile();
  const pharmacy = getPharmacySettings();
  const reception = getReceptionSettings();
  const hospitalRevenue = getHospitalRevenueSettings();
  const activeUnits = listMedicalUnits().filter((u) => u.active);
  const activePartnerships = listHospitalPartnerships({ status: "active" });

  const establishment =
    profile.name.trim().length >= 3 &&
    profile.address.trim().length >= 3 &&
    profile.phone.trim().length >= 6;

  return {
    establishment,
    medicalUnits: activeUnits.length >= 2,
    surgeryUnit: hasSurgeryMedicalUnit(),
    reception: reception.generalConsultationFee > 0,
    hospitalRevenue:
      hospitalRevenue.emergencyVisitFee > 0 &&
      hospitalRevenue.hospitalizationDailyFee > 0 &&
      hospitalRevenue.nursingCareFee > 0 &&
      hospitalRevenue.surgeryProcedureFee > 0 &&
      hospitalRevenue.ambulanceTransportFee > 0,
    pharmacy: Boolean(pharmacy.invoicePrefix.trim()) && Boolean(pharmacy.currency),
    insurance: activePartnerships.length > 0 || state.insuranceSkipped,
  };
}

export function fetchHospitolSetupStatus(): HospitolSetupStatus {
  const state = readState();
  const checks = computeSetupChecks(state);
  const checkValues = Object.values(checks);
  const doneCount = checkValues.filter(Boolean).length;
  const progressPercent = Math.round((doneCount / checkValues.length) * 100);

  return {
    complete: state.setupCompleted,
    currentStep: state.currentStep,
    checks,
    progressPercent,
  };
}

export function updateSetupProgress(currentStep: number): HospitolSetupState {
  const state = readState();
  const next: HospitolSetupState = {
    ...state,
    currentStep: Math.min(6, Math.max(1, currentStep)),
  };
  writeState(next);
  return next;
}

export function skipInsuranceSetupStep(): HospitolSetupState {
  const state = readState();
  const next: HospitolSetupState = {
    ...state,
    insuranceSkipped: true,
    currentStep: Math.max(state.currentStep, 6),
  };
  writeState(next);
  return next;
}

export function completeHospitolSetup(): HospitolSetupState {
  const state = readState();
  const next: HospitolSetupState = {
    ...state,
    setupCompleted: true,
    currentStep: 6,
    completedAt: new Date().toISOString(),
  };
  writeState(next);
  return next;
}

export function resetHospitolSetup(): HospitolSetupState {
  writeState({ ...DEFAULT_STATE });
  return readState();
}
